/**
 * AddGroupDialog Component
 *
 * Dialog for creating a new Schema Group within a Product.
 * Groups are organizational containers for related schema memberships.
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { groupApi, Product, CreateGroupInput } from '@/services/api/groupApi';
import { serverApi, DatabaseConnection } from '@/services/api/serverApi';
import { FOREST_GREEN } from '@/styles/tokens';
import * as errorUtils from '@/utils/errors';

interface AddGroupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: Product | null;
  onSuccess: () => void;
}

export const AddGroupDialog: React.FC<AddGroupDialogProps> = ({
  open,
  onOpenChange,
  product,
  onSuccess,
}) => {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [selectedDatabaseId, setSelectedDatabaseId] = useState<string>('');
  const [databaseConnections, setDatabaseConnections] = useState<DatabaseConnection[]>([]);
  const [isLoadingDatabases, setIsLoadingDatabases] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch database connections when dialog opens
  useEffect(() => {
    if (open) {
      fetchDatabases();
    }
  }, [open]);

  const fetchDatabases = async () => {
    setIsLoadingDatabases(true);
    try {
      const connections = await serverApi.listConnections();
      setDatabaseConnections(connections);
    } catch {
      // Non-critical - continue with empty list
      setDatabaseConnections([]);
    } finally {
      setIsLoadingDatabases(false);
    }
  };

  // Auto-generate slug from name
  const handleNameChange = useCallback((value: string) => {
    setName(value);
    const generatedSlug = value
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
    setSlug(generatedSlug);
  }, []);

  // Reset form when dialog closes
  const handleOpenChange = useCallback((isOpen: boolean) => {
    if (!isOpen) {
      setName('');
      setSlug('');
      setDescription('');
      setSelectedDatabaseId('');
      setError(null);
    }
    onOpenChange(isOpen);
  }, [onOpenChange]);

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    if (!name.trim()) {
      setError('Group name is required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const input: CreateGroupInput = {
        name: name.trim(),
        slug: slug.trim() || undefined,
        description: description.trim() || undefined,
        productId: product?.id,
        databaseConnectionId: selectedDatabaseId || undefined,
      };

      await groupApi.createGroup(input);
      handleOpenChange(false);
      onSuccess();
    } catch (err: unknown) {
      const errorMessage =
        errorUtils.getErrorMessageOrFallback(err, 'Failed to create group');
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }, [name, slug, description, selectedDatabaseId, product, handleOpenChange, onSuccess]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            Create Schema Group
            {product && (
              <span className="text-sm font-normal text-muted-foreground ml-2">
                in {product.name}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="group-name">Name *</Label>
            <Input
              id="group-name"
              placeholder="e.g., Fermentation Monitoring, Well Sensors"
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              A descriptive name for this schema group
            </p>
          </div>

          {/* Slug */}
          <div className="space-y-2">
            <Label htmlFor="group-slug">Slug</Label>
            <Input
              id="group-slug"
              placeholder="Auto-generated from name"
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            />
            <p className="text-xs text-muted-foreground">
              URL-safe identifier used in collection names
            </p>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="group-description">Description</Label>
            <Input
              id="group-description"
              placeholder="e.g., Schemas for monitoring fermentation tanks"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {/* Database Connection */}
          <div className="space-y-2">
            <Label htmlFor="group-database">Database Connection</Label>
            <Select
              value={selectedDatabaseId || '__default__'}
              onValueChange={(v) => setSelectedDatabaseId(v === '__default__' ? '' : v)}
              disabled={isLoadingDatabases}
            >
              <SelectTrigger id="group-database">
                <SelectValue placeholder={isLoadingDatabases ? "Loading..." : "Default Connection"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__default__">Default Connection</SelectItem>
                {databaseConnections.filter((db) => !db.isDefault).map((db) => (
                  <SelectItem key={db.id} value={db.id}>
                    {db.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              All schemas in this group will use this database
            </p>
          </div>

          {/* Product Info */}
          {product && (
            <div className="p-3 bg-muted/50 rounded-lg">
              <div className="text-sm">
                <span className="font-medium">Product:</span>{' '}
                <span className="text-muted-foreground">{product.name}</span>
              </div>
              {product.description && (
                <div className="text-xs text-muted-foreground mt-1">
                  {product.description}
                </div>
              )}
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="p-3 rounded-lg bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-400 text-sm">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="text-white"
            style={{ backgroundColor: FOREST_GREEN.DEFAULT }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = FOREST_GREEN.hover)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = FOREST_GREEN.DEFAULT)}
          >
            {isSubmitting ? 'Creating...' : 'Create Group'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddGroupDialog;
